import { easyCheckRequest } from '../api/config';
import easyCheckApi from '../api/config';
import * as XLSX from 'xlsx-js-style';

export const bankImportService = {
  // Generate Excel template for bank import
  generateTemplate: () => {
    const templateData = [
      {
        'Account Number': '1234567890',
        'Account Name': 'Sample Bank Account',
        'Currency': 'AED'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData, {
      header: Object.keys(templateData[0])
    });

    // Add instructions sheet
    const instructions = [
      { Field: 'Account Number', Description: 'Unique account number (Required)', Example: '1234567890' },
      { Field: 'Account Name', Description: 'Name of the bank account (Required)', Example: 'Main Operating Account' },
      { Field: 'Currency', Description: 'Account currency (Required)', Example: 'AED, OMR, SAR, BHD, KWD, QAR, EGP, EUR' }
    ];

    const instructionsWs = XLSX.utils.json_to_sheet(instructions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
    XLSX.utils.book_append_sheet(wb, ws, 'Bank Template');

    // Set column widths
    const wscols = [
      { wch: 20 }, // Account Number
      { wch: 30 }, // Account Name
      { wch: 15 }  // Currency
    ];
    ws['!cols'] = wscols;

    const instructionsCols = [
      { wch: 20 }, // Field
      { wch: 50 }, // Description
      { wch: 30 }  // Example
    ];
    instructionsWs['!cols'] = instructionsCols;

    return wb;
  },

  // Download template file
  downloadTemplate: () => {
    const wb = bankImportService.generateTemplate();
    const fileName = `Bank_Master_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  },

  // Validate Excel file structure
  validateFileStructure: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          if (!workbook.SheetNames.includes('Bank Template')) {
            reject(new Error('Excel file must contain a "Bank Template" sheet'));
            return;
          }

          const worksheet = workbook.Sheets['Bank Template'];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            reject(new Error('Excel file is empty'));
            return;
          }

          const requiredFields = ['Account Number', 'Account Name', 'Currency'];
          const firstRow = jsonData[0];
          const missingFields = requiredFields.filter(field => !(field in firstRow));

          if (missingFields.length > 0) {
            reject(new Error(`Missing required columns: ${missingFields.join(', ')}`));
            return;
          }

          resolve({
            isValid: true,
            data: jsonData,
            rowCount: jsonData.length
          });
        } catch (error) {
          reject(new Error('Invalid Excel file format'));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  },

  // Process and import bank data
  importBanks: async (file, onProgress) => {
    try {
      // First validate the file
      const validation = await bankImportService.validateFileStructure(file);
      
      if (!validation.isValid) {
        throw new Error('File validation failed');
      }

      const { data } = validation;
      const results = [];
      const totalRows = data.length;

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNumber = i + 1;

        try {
          // Update progress
          if (onProgress) {
            onProgress({
              current: i + 1,
              total: totalRows,
              percentage: Math.round(((i + 1) / totalRows) * 100),
              status: `Processing row ${i + 1} of ${totalRows}...`
            });
          }

          // Validate required fields
          const validationErrors = [];
          if (!row['Account Number']) validationErrors.push('Account Number is required');
          if (!row['Account Name']) validationErrors.push('Account Name is required');
          if (!row['Currency']) validationErrors.push('Currency is required');

          // Validate currency
          const validCurrencies = ['AED', 'OMR', 'SAR', 'BHD', 'KWD', 'QAR', 'EGP', 'EUR'];
          if (row['Currency'] && !validCurrencies.includes(row['Currency'])) {
            validationErrors.push(`Invalid currency. Must be one of: ${validCurrencies.join(', ')}`);
          }

          if (validationErrors.length > 0) {
            results.push({
              rowNumber,
              data: row,
              status: 'FAILED',
              errors: validationErrors,
              message: validationErrors.join('; ')
            });
            continue;
          }

          // Prepare bank data for API - only required fields
          const bankData = {
            accountNumber: row['Account Number'],
            accountName: row['Account Name'],
            currency: row['Currency']
          };

          // Call API to create bank
          const result = await easyCheckRequest(() => 
            easyCheckApi.post('/banks', bankData)
          );

          if (result.success) {
            results.push({
              rowNumber,
              data: row,
              status: 'SUCCESS',
              message: 'Bank account created successfully',
              bankId: result.data?.id
            });
          } else {
            results.push({
              rowNumber,
              data: row,
              status: 'FAILED',
              message: result.message || 'Failed to create bank account',
              errors: [result.message || 'Unknown error']
            });
          }

        } catch (error) {
          results.push({
            rowNumber,
            data: row,
            status: 'FAILED',
            message: error.message || 'Unexpected error occurred',
            errors: [error.message || 'Unexpected error']
          });
        }

        // Add a small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      return {
        success: true,
        results,
        summary: {
          total: totalRows,
          successful: results.filter(r => r.status === 'SUCCESS').length,
          failed: results.filter(r => r.status === 'FAILED').length
        }
      };

    } catch (error) {
      throw new Error(`Import failed: ${error.message}`);
    }
  },

  // Generate result file with import status
  generateResultFile: (results, originalFileName) => {
    const resultData = results.map(result => ({
      'Row Number': result.rowNumber,
      'Account Number': result.data['Account Number'],
      'Account Name': result.data['Account Name'],
      'Currency': result.data['Currency'],
      'Import Status': result.status,
      'Status Message': result.message,
      'Errors': result.errors ? result.errors.join('; ') : ''
    }));

    const ws = XLSX.utils.json_to_sheet(resultData);
    
    // Set column widths
    const wscols = [
      { wch: 12 }, // Row Number
      { wch: 20 }, // Account Number
      { wch: 30 }, // Account Name
      { wch: 15 }, // Currency
      { wch: 15 }, // Import Status
      { wch: 50 }, // Status Message
      { wch: 50 }  // Errors
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Import Results');

    // Generate filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T');
    const resultFileName = `Bank_Import_Results_${timestamp[0]}_${timestamp[1].split('.')[0]}.xlsx`;

    return { workbook: wb, fileName: resultFileName };
  },

  // Download result file
  downloadResultFile: (results, originalFileName) => {
    const { workbook, fileName } = bankImportService.generateResultFile(results, originalFileName);
    XLSX.writeFile(workbook, fileName);
  }
}; 